/**
 * Standalone SVG export (PRD §4.8).
 *
 * The charts are styled with CSS custom properties and classes, none of which
 * survive being pulled out of the document — a naively serialised SVG opens as
 * black shapes on a transparent background. So the exporter resolves every
 * inherited paint and text property to a literal attribute on the clone, and
 * paints an explicit background rectangle rather than relying on the page
 * beneath.
 *
 * Colours are read from the LIVE element, so an export made in dark mode is a
 * dark chart and one made in light mode is a light chart. That is the honest
 * behaviour: the file matches what the user was looking at when they clicked.
 */

/** Properties that must be resolved; everything else is either inherited safely or irrelevant. */
const PAINT_PROPERTIES = [
  'fill',
  'stroke',
  'stroke-width',
  'opacity',
  'fill-opacity',
  'stroke-opacity',
  'font-family',
  'font-size',
  'font-weight',
  'font-variant-numeric',
  'text-anchor',
  'letter-spacing',
] as const;

function inlineStyles(live: Element, clone: Element): void {
  const computed = getComputedStyle(live);
  const parts: string[] = [];

  for (const prop of PAINT_PROPERTIES) {
    const value = computed.getPropertyValue(prop);
    if (!value || value === 'none' && prop !== 'fill' && prop !== 'stroke') continue;
    // `normal` and default weights add noise without changing rendering.
    if (value === 'normal' && prop !== 'font-variant-numeric') continue;
    parts.push(`${prop}:${value}`);
  }

  if (parts.length > 0) clone.setAttribute('style', parts.join(';'));
  clone.removeAttribute('class');

  const liveKids = live.children;
  const cloneKids = clone.children;
  for (let i = 0; i < liveKids.length && i < cloneKids.length; i++) {
    inlineStyles(liveKids[i], cloneKids[i]);
  }
}

/** Serialise an on-page `<svg>` into a self-contained SVG document. */
export function serializeSvg(svg: SVGSVGElement, backgroundColor?: string): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineStyles(svg, clone);

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  // A viewBox without explicit dimensions renders at an arbitrary size in
  // viewers that are not a browser. Pin both from the viewBox.
  const viewBox = clone.getAttribute('viewBox');
  if (viewBox) {
    const [, , w, h] = viewBox.split(/[\s,]+/).map(Number);
    if (Number.isFinite(w) && Number.isFinite(h)) {
      clone.setAttribute('width', String(w));
      clone.setAttribute('height', String(h));
    }
  }

  const background =
    backgroundColor ??
    getComputedStyle(svg).getPropertyValue('--surface-1').trim() ??
    '#ffffff';

  if (background) {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', '0');
    rect.setAttribute('y', '0');
    rect.setAttribute('width', '100%');
    rect.setAttribute('height', '100%');
    rect.setAttribute('fill', background);
    clone.insertBefore(rect, clone.firstChild);
  }

  return `<?xml version="1.0" encoding="UTF-8"?>\n${new XMLSerializer().serializeToString(clone)}`;
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately can cancel the download in some browsers; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function safeFilename(name: string, extension: string): string {
  const base = name.replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '') || 'chart';
  return `${base}.${extension}`;
}

/** Export the first `<svg>` inside `container` as a downloadable file. */
export function exportChartSvg(container: HTMLElement | null, name: string): void {
  const svg = container?.querySelector('svg');
  if (!svg) return;
  const text = serializeSvg(svg as SVGSVGElement);
  download(new Blob([text], { type: 'image/svg+xml;charset=utf-8' }), safeFilename(name, 'svg'));
}

/** Export arbitrary rows as CSV, for the data-table views. */
export function exportCsv(rows: (string | number)[][], name: string): void {
  const escape = (cell: string | number): string => {
    const s = String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = rows.map((r) => r.map(escape).join(',')).join('\n');
  download(new Blob([text], { type: 'text/csv;charset=utf-8' }), safeFilename(name, 'csv'));
}
